"use client"

import { KNOWLEDGE_SOURCE_PROVIDERS } from "@/lib/knowledge-sources/providers"
import { useState } from "react"
import { useTranslations } from "next-intl"
import { WebsiteImportForm } from "@/components/onboarding/website-import-form"
import { DocumentImportForm } from "@/components/onboarding/document-import-form"
import { SocialImportForm } from "@/components/onboarding/social-import-form"
import { ManualKnowledgeForm } from "@/components/onboarding/manual-knowledge-form"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useOnboardingKnowledgeLimits } from "@/hooks/queries/onboarding-knowledge-limits"
import { KnowledgeSourceImportForm } from "@/components/knowledge-sources/import-form"
import { KnowledgeSourceProviderIcon } from "@/components/knowledge-sources/provider-icon"

export type KnowledgeImportSource = "website" | "instagram" | "facebook" | "documents" | "manual" | "google_drive"
export type KnowledgeImportJob = { id: string; source: "website" | "instagram" | "facebook" }

export function KnowledgeImportOptions({ initialSource, onDocumentCreated, onDocumentsImported, onImported, onJobAccepted, onViewProgress, onSkip }: {
  initialSource: KnowledgeImportSource
  onDocumentCreated: (id: string) => Promise<void>
  onDocumentsImported: (ids: string[]) => Promise<void>
  onImported: () => void
  onJobAccepted: (job: KnowledgeImportJob) => Promise<void>
  onViewProgress: () => void
  onSkip: () => void
}) {
  const t = useTranslations("AgentCreation")
  const choice = useTranslations("AgentCreationKnowledgeChoice")
  const limits = useOnboardingKnowledgeLimits("not_selected")
  const [source, setSource] = useState<KnowledgeImportSource>(initialSource)

  return (
    <div className="min-w-0 space-y-3">
      <h2 className="text-sm font-medium"><span>{choice("importFrom")}</span></h2>
      <Tabs value={source} onValueChange={value => setSource(value as KnowledgeImportSource)}>
        <TabsList className="h-auto max-w-full flex-wrap justify-start [&>button]:min-h-10">
          <TabsTrigger value="website"><span>{t("website")}</span></TabsTrigger>
          <TabsTrigger value="instagram"><span>Instagram</span></TabsTrigger>
          <TabsTrigger value="facebook"><span>Facebook</span></TabsTrigger>
          <TabsTrigger value="documents"><span>{choice("documentUpload")}</span></TabsTrigger>
          <TabsTrigger value="google_drive" className="gap-2"><KnowledgeSourceProviderIcon provider="google_drive" /><span>{KNOWLEDGE_SOURCE_PROVIDERS[0].name}</span></TabsTrigger>
          <TabsTrigger value="manual"><span>{t("manual")}</span></TabsTrigger>
        </TabsList>
        <TabsContent value="website">
          <WebsiteImportForm
            isDefault={false}
            maxPagesLimit={limits.maxPagesLimit}
            balanceDepleted={limits.balanceDepleted}
            showSkipAction={false}
            onSkip={onSkip}
            onViewProgress={onViewProgress}
            onImported={result => onJobAccepted({ id: result.jobId, source: "website" })}
          />
        </TabsContent>
        <TabsContent value="instagram">
          <SocialImportForm platform="instagram" isDefault={false} disabled={limits.balanceDepleted} onImported={result => onJobAccepted({ id: result.jobId, source: "instagram" })} />
        </TabsContent>
        <TabsContent value="facebook">
          <SocialImportForm platform="facebook" isDefault={false} disabled={limits.balanceDepleted} onImported={result => onJobAccepted({ id: result.jobId, source: "facebook" })} />
        </TabsContent>
        <TabsContent value="documents">
          <DocumentImportForm isDefault={false} maxFiles={limits.maxDocumentFiles} balanceDepleted={limits.balanceDepleted} showSkipAction={false} onSkip={onSkip} onDocumentCreated={onDocumentCreated} onImported={onImported} />
        </TabsContent>
        <TabsContent value="manual">
          <ManualKnowledgeForm isDefault={false} balanceDepleted={limits.balanceDepleted} onDocumentCreated={onDocumentCreated} onImported={onImported} />
        </TabsContent>
        <TabsContent value="google_drive">
          <KnowledgeSourceImportForm onImported={async ids => {
            await onDocumentsImported(ids)
            onImported()
          }} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
